import { useMemo } from "react";
import { useAccessibility, useI18N } from "common/hooks";

import OnboardingDescribedOptionCard from "../components/OnboardingDescribedOptionCard";
import OnboardingModal from "../components/OnboardingModal";
import OnboardingPrimaryButton from "../components/OnboardingPrimaryButton";
import OnboardingStepHeader from "../components/OnboardingStepHeader";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingStepNavigation } from "../state/useOnboardingStepNavigation";
import type { OnboardingHearAbout } from "../types";

type HearAboutRow = {
  kind: OnboardingHearAbout["kind"];
  id: number;
  label: string;
  description?: string;
  allowsOtherText: boolean;
};

/**
 * "How did you hear about us?" (`16133:189205`) — step 4 of 4 on sign-up, and
 * step 4 of 5 on the docked sheet and modal for a signed-out visitor, whose
 * answer then goes out with their sign-up - @see
 * `ONBOARDING_SEQUENCE_SIGNED_OUT`.
 *
 * **Rows come from the backend, not the frame.** `/accessibility/load` →
 * `how_do_you_hear_about_groups` is what the sign-up endpoint stores against,
 * so the frame's copy ("Social media & Creators", "Word of mouth", …) is
 * illustrative: each row is a group, titled with its name, with its options
 * listed underneath as the frame lists examples. Until every option has a
 * group the backend sends no groups at all, and the step falls back to the flat
 * option list — @see `HearAboutUsGroup`.
 *
 * **One answer**, as the frame's radios and "Select one to continue" say.
 *
 * **The free-text field only opens on a group that keeps it.** The frame draws
 * "Optional" under the first row, but the backend stores the text only for a
 * group with `allows_other_text` ("Others" today); asking everywhere would
 * collect answers that are silently thrown away.
 *
 * Continue waits for a pick — unless there are no rows to pick from, in which
 * case the question is skipped rather than turned into a dead end.
 */
const HearAboutStep = () => {
  const i18n = useI18N();
  const { answers, updateAnswers, dismiss } = useOnboardingFlow();
  const { stepNumber, totalSteps, goNext } = useOnboardingStepNavigation();
  const {
    howDoYouHearAboutGroups,
    howDoYouHearAboutOptions,
    isAccessibilityLoading,
  } = useAccessibility();

  const title = i18n.get("onboardingHearAboutTitle");
  const { hearAbout } = answers;

  const rows = useMemo<HearAboutRow[]>(() => {
    if (howDoYouHearAboutGroups.length > 0) {
      return howDoYouHearAboutGroups.map((group) => ({
        kind: "group",
        id: group.id,
        label: group.name,
        // The admin's `sub_heading` when there is one ("Instagram, Facebook,
        // Tiktok, LinkedIn" in the frame), else the group's own options -
        // the backend sends none yet, and every group allows free text now,
        // so keying this off `allows_other_text` left every row bare.
        description:
          group.sub_heading ||
          group.options.map((option) => option.title).join(", "),
        allowsOtherText: group.allows_other_text,
      }));
    }
    return (howDoYouHearAboutOptions as { id: number; title: string }[]).map(
      (option) => ({
        kind: "option",
        id: option.id,
        label: option.title,
        allowsOtherText: false,
      }),
    );
  }, [howDoYouHearAboutGroups, howDoYouHearAboutOptions]);

  const isSelected = (row: HearAboutRow) =>
    hearAbout?.kind === row.kind && hearAbout.id === row.id;

  const select = (row: HearAboutRow) => {
    if (isSelected(row)) return;
    updateAnswers({
      hearAbout: { kind: row.kind, id: row.id, label: row.label },
    });
  };

  const canContinue =
    !!hearAbout || (!isAccessibilityLoading && rows.length === 0);

  return (
    <OnboardingModal
      onClose={dismiss}
      ariaLabel={title}
      footer={
        <OnboardingPrimaryButton
          label={i18n.get("continue")}
          disabled={!canContinue}
          onClick={goNext}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <OnboardingStepHeader
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title={title}
          subtitle={i18n.get("onboardingHearAboutSubtitle")}
        />

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="sr-only">{title}</legend>

          {rows.map((row) => (
            <OnboardingDescribedOptionCard
              key={`${row.kind}-${row.id}`}
              name="onboarding-hear-about"
              label={row.label}
              description={row.description}
              selected={isSelected(row)}
              onSelect={() => select(row)}
            >
              {row.allowsOtherText ? (
                <input
                  type="text"
                  value={hearAbout?.otherText ?? ""}
                  onChange={(event) =>
                    updateAnswers({
                      hearAbout: {
                        kind: row.kind,
                        id: row.id,
                        label: row.label,
                        otherText: event.target.value,
                      },
                    })
                  }
                  maxLength={1000}
                  placeholder={i18n.get("onboardingHearAboutOtherPlaceholder")}
                  aria-label={i18n.get("onboardingHearAboutOtherLabel")}
                  className="focus-blue label-m-medium min-h-12 w-full rounded-lg border border-border-token-neutral-subtle bg-bg-neutral-secondary-default px-4 py-3 text-fg-neutral-strong placeholder:text-fg-neutral-subtle"
                />
              ) : null}
            </OnboardingDescribedOptionCard>
          ))}
        </fieldset>

        <p aria-live="polite" className="sr-only">
          {isAccessibilityLoading ? i18n.get("loading") : ""}
        </p>
      </div>
    </OnboardingModal>
  );
};

export default HearAboutStep;
